import React, { ReactNode } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

export interface DashboardFilterSupportProp {
  timeRange?: boolean;
  scope?: boolean;
}

export interface DraggableWidgetProps {
  id: string;
  title: string;
  children: ReactNode;
  isEditing?: boolean;
  onRemove?: () => void;
  onSettingsClick?: () => void;
  dragHandle?: ReactNode;
  /** When set, show icon(s): calendar for Time range, person for Scope (My work / Team) */
  dashboardFilterSupport?: DashboardFilterSupportProp | null;
}

const CalendarIconSmall = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const PersonIconSmall = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const DraggableWidget: React.FC<DraggableWidgetProps> = ({
  id,
  title,
  children,
  isEditing = false,
  onRemove,
  onSettingsClick,
  dragHandle,
  dashboardFilterSupport,
}) => {
  const { colors } = useTheme();
  const usesTimeRange = dashboardFilterSupport?.timeRange;
  const usesScope = dashboardFilterSupport?.scope;
  const showFilterBadge = usesTimeRange || usesScope;

  return (
    <div
      className={`${colors.surface} rounded-2xl shadow-md shadow-black/5 hover:shadow-lg hover:shadow-black/5 transition-shadow duration-200 border ${colors.border} overflow-hidden h-full w-full min-w-0 flex flex-col relative max-w-none`}
      data-widget-id={id}
    >
      {/* Filter badge: top-left when not editing so it never overlaps "View All" in widget header */}
      {showFilterBadge && !isEditing && (
        <div
          className={cn(
            'absolute top-3 left-3 z-[1] flex items-center gap-1.5 rounded-md px-2 py-1 border',
            colors.surfaceHover,
            colors.border,
            colors.primaryFg,
          )}
          title={[usesTimeRange && 'Time range', usesScope && 'Scope (My work / Team)'].filter(Boolean).join(', ')}
        >
          {usesTimeRange && <CalendarIconSmall className="w-3.5 h-3.5 flex-shrink-0" />}
          {usesScope && <PersonIconSmall className="w-3.5 h-3.5 flex-shrink-0" />}
        </div>
      )}
      {/* Edit mode: floating corner controls (no header bar) - touch-friendly on mobile */}
      {isEditing && (dragHandle || onRemove || onSettingsClick) && (
        <div className={cn('absolute top-2 right-2 z-[2] flex items-center gap-0.5 rounded-xl border shadow-lg backdrop-blur-sm py-1 pl-1.5 pr-1', colors.border, colors.surface)}>
          {showFilterBadge && (
            <span className={cn('flex items-center gap-1 mr-0.5 pr-1.5 border-r min-h-[44px] sm:min-h-0', colors.border, colors.primaryFg)} title={[usesTimeRange && 'Time range', usesScope && 'Scope'].filter(Boolean).join(', ')}>
              {usesTimeRange && <CalendarIconSmall className="w-3.5 h-3.5" />}
              {usesScope && <PersonIconSmall className="w-3.5 h-3.5" />}
            </span>
          )}
          {dragHandle && (
            <span className={cn('cursor-grab active:cursor-grabbing touch-none flex items-center justify-center p-2.5 sm:p-1.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 rounded-lg drag-handle', colors.textMuted, colors.interactiveHover)}>
              {dragHandle}
            </span>
          )}
          {onSettingsClick && (
            <button
              type="button"
              onClick={onSettingsClick}
              className={cn('p-2.5 sm:p-1.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 rounded-lg transition-colors flex items-center justify-center', colors.textMuted, colors.interactiveHover, 'hover:opacity-100')}
              title="Widget settings"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="p-2.5 sm:p-1.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center justify-center"
              title="Remove widget"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}
      {/* Content - reserve left padding when filter badge is visible so widget title never overlaps badge */}
      <div className={`p-4 sm:p-5 flex-1 min-h-0 overflow-hidden flex flex-col ${showFilterBadge && !isEditing ? 'pl-16 sm:pl-[4.5rem]' : ''}`}>
        <div className="flex-1 min-h-0 flex flex-col">{children}</div>
      </div>
    </div>
  );
};

export default DraggableWidget;
